import { enqueueSnackbar } from 'notistack';
import { useMemo } from 'react';

import { Box, Card, CardContent, CardHeader, Chip, Stack, Typography, useTheme } from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import {
  useGetCandidateSummaryFieldsQuery,
  useUpdateCandidateSummaryFieldsMutation,
} from 'src/services/settings/candidate-summary-fields.service';
import {
  CANDIDATE_SUMMARY_FIELDS,
  MAX_SUMMARY_FIELDS,
  resolveSummaryFieldKeys,
} from 'src/sections/candidates/candidate-summary-meta';

import Iconify from 'src/components/iconify';
import DataListColumnPicker from 'src/components/data-list/data-list-column-picker';
import { PickerColumn } from 'src/components/data-list/data-list.types';

/**
 * Org-level candidate summary-line fields (spec item 17, JT1-1538). Admins pick up to four fields from
 * the shared registry (the same fields that drive the list columns); the choice drives the metadata
 * shown under a candidate's name on the profile header and the record panel org-wide. Reuses the list
 * column picker so the field-selection UI stays consistent with JT1-1528.
 */
export default function CandidateSummaryFieldsSetting() {
  const { t } = useTranslate();
  const theme = useTheme();
  const { account } = useAuthContext();

  const { data } = useGetCandidateSummaryFieldsQuery();
  const [updateSummaryFields] = useUpdateCandidateSummaryFieldsMutation();

  const selectedKeys = resolveSummaryFieldKeys(data?.fields);

  const canEdit = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  // Picker model: selected fields first, in the saved order, then the rest of the catalogue.
  const model: PickerColumn[] = useMemo(() => {
    const toPicker = (key: string, visible: boolean): PickerColumn | null => {
      const field = CANDIDATE_SUMMARY_FIELDS.find((f) => f.key === key);
      return field ? { key: field.key, label: field.label(t), visible, fixed: false } : null;
    };

    const selected = selectedKeys
      .map((key) => toPicker(key, true))
      .filter((c): c is PickerColumn => c !== null);
    const rest = CANDIDATE_SUMMARY_FIELDS
      .filter((f) => !selectedKeys.includes(f.key))
      .map((f) => toPicker(f.key, false)!);

    return [...selected, ...rest];
  }, [selectedKeys, t]);

  const selectedLabels = useMemo(
    () =>
      selectedKeys
        .map((key) => CANDIDATE_SUMMARY_FIELDS.find((f) => f.key === key)?.label(t))
        .filter((label): label is string => !!label),
    [selectedKeys, t],
  );

  const handleSave = async (next: PickerColumn[]) => {
    const fields = next
      .filter((c) => c.visible)
      .map((c) => c.key)
      .slice(0, MAX_SUMMARY_FIELDS);

    try {
      await updateSummaryFields({ fields }).unwrap();
      enqueueSnackbar(
        t('admin-organization.settings.candidate_summary_fields.update.success', { defaultValue: 'Summary line updated' }),
        { variant: 'success' },
      );
    } catch (e) {
      enqueueSnackbar(
        t('admin-organization.settings.candidate_summary_fields.update.error', { defaultValue: 'Could not update the summary line' }),
        { variant: 'error' },
      );
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Card sx={{ width: '100%', position: 'relative', boxShadow: 0 }}>
        <CardHeader
          avatar={
            <Iconify
              icon="solar:user-id-bold"
              sx={{ width: 30, height: 30, color: theme.palette.primary.main }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t('admin-organization.settings.candidate_summary_fields.title', { defaultValue: 'Candidate summary line' })}
            </Typography>
          }
        />

        <CardContent>
          <Stack direction="column" gap={2} sx={{ mt: 1, width: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              {t('admin-organization.settings.candidate_summary_fields.helper_text', {
                count: MAX_SUMMARY_FIELDS,
                defaultValue: `Choose up to ${MAX_SUMMARY_FIELDS} fields shown under a candidate's name across their profile and the record panel.`,
              })}
            </Typography>

            <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
              {selectedLabels.length > 0 ? (
                selectedLabels.map((label) => <Chip key={label} label={label} size="small" />)
              ) : (
                <Typography variant="body2" color="text.disabled">
                  {t('admin-organization.settings.candidate_summary_fields.none_selected', { defaultValue: 'No fields selected' })}
                </Typography>
              )}
            </Stack>

            {canEdit && (
              <Box>
                <DataListColumnPicker
                  model={model}
                  onSave={handleSave}
                  maxVisible={MAX_SUMMARY_FIELDS}
                  buttonLabel={t('admin-organization.settings.candidate_summary_fields.edit_button', { defaultValue: 'Edit fields' })}
                />
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
